/** Typed fetch wrapper for the Galeway API. Cookies are HttpOnly and sent automatically. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Array<{ path: string; message: string }> | unknown,
    public requestId?: string,
  ) {
    super(message);
  }
}

type Json = Record<string, unknown> | unknown[];

const COMPANY_KEY = "galeway.company";

/** The company the user is viewing (company switcher). The server validates it against their memberships. */
export function selectedCompanyId(): string | null {
  try {
    return localStorage.getItem(COMPANY_KEY);
  } catch {
    return null;
  }
}

export function selectCompany(id: string) {
  try {
    localStorage.setItem(COMPANY_KEY, id);
  } catch {
    /* storage unavailable: the server falls back to the user's first company */
  }
}

async function request<T>(method: string, path: string, body?: Json): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      ...(selectedCompanyId() ? { "x-galeway-company": selectedCompanyId()! } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await response.text();
  const data = text ? safeParse(text) : undefined;
  if (!response.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown }; requestId?: string } | undefined) ?? {};
    throw new ApiError(
      response.status,
      err.error?.code ?? "http_error",
      err.error?.message ?? `Request failed (${response.status})`,
      err.error?.details,
      err.requestId,
    );
  }
  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | undefined | null>) => {
    const qs = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : "";
    return request<T>("GET", qs ? `${path}?${qs}` : path);
  },
  post: <T>(path: string, body: Json = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: Json = {}) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: Json = {}) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) return {};
  return Object.fromEntries((error.details as Array<{ path: string; message: string }>).map((d) => [d.path, d.message]));
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}
